import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type DbCompound = {
  id: string;
  name: string;
  slug: string;
  aliases: string[] | null;
  category: string;
  description: string | null;
  featured: boolean;
  mechanism_of_action: string | null;
  research_stage: string | null;
  approval_status: string | null;
  side_effects: string | null;
  clinical_studies_summary: string | null;
  half_life: string | null;
  molecular_weight: string | null;
  amino_acid_sequence: string | null;
  administration_routes: string[] | null;
  stacking_notes: string | null;
  popularity_score: number | null;
  view_count: number | null;
  dose_range_mcg: string | null;
  dosing_frequency: string | null;
  cycle_length: string | null;
  common_vial_sizes: string[] | null;
  beginner_protocol: string | null;
  intermediate_protocol: string | null;
  advanced_protocol: string | null;
  weight_based_dosing: string | null;
  dosage_notes: string | null;
  // Wiki columns
  cas_number: string | null;
  iupac_name: string | null;
  chemical_formula: string | null;
  molecular_mass: string | null;
  structure_image_url: string | null;
  image_url: string | null;
  potential_benefits: string[] | null;
  research_fields: string[] | null;
  safety_profile: Record<string, any> | null;
  dosing_summary: Record<string, any> | null;
  titration_schedule: Record<string, any>[] | null;
  reconstitution_notes: string | null;
  trial_phase: string | null;
  pubmed_query: string | null;
  pubmed_count: number | null;
  pubmed_last_checked: string | null;
  pubchem_cid: number | null;
  product_image_url: string | null;
  interactions: Record<string, any>[] | null;
  contraindications: string | null;
  what_to_expect: Record<string, any>[] | null;
  faq_items: { q: string; a: string }[] | null;
  updated_at: string;
  created_at: string;
};

export type DbVendor = {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  logo_url: string | null;
  brand_color: string | null;
  short_name: string | null;
  description: string | null;
  default_coupon: string | null;
  payment_methods: string[] | null;
  shipping_regions: string[] | null;
  website: string | null;
  ref_param: string | null;
  active: boolean;
  priority: number;
  rating: number | null;
};

export type DbListing = {
  id: string;
  compound_id: string;
  vendor_id: string;
  title: string;
  form_type: string;
  quantity: string | null;
  total_mg: number;
  concentration: string | null;
  price: number;
  coupon_code: string | null;
  affiliate_url: string | null;
  payment_methods: string[] | null;
  stock_status: string;
  active: boolean;
  /** ISO timestamp of last DB write — surfaces freshness on ad LPs */
  updated_at?: string;
  created_at?: string;
};

export type DbDeal = {
  id: string;
  vendor_id: string | null;
  vendor_name: string;
  discount: string;
  coupon_code: string | null;
  description: string | null;
  expires_at: string | null;
  active: boolean;
};

// Lightweight column projection for list queries. Omits heavy long-form text
// and JSONB fields (description is kept — used for card previews — but full
// body content like mechanism_of_action, clinical_studies_summary, protocols,
// FAQ, etc. are ~50–80KB per row and only needed on individual compound pages).
// Per-row payload: ~2–5KB instead of ~50–80KB.
const COMPOUND_LIST_COLUMNS = [
  "id", "name", "slug", "aliases", "category", "description", "featured",
  "research_stage", "approval_status", "trial_phase",
  "half_life", "molecular_weight", "molecular_mass", "amino_acid_sequence",
  "administration_routes", "dose_range_mcg", "dosing_frequency", "cycle_length",
  "common_vial_sizes", "popularity_score", "view_count",
  "cas_number", "iupac_name", "chemical_formula",
  "structure_image_url", "image_url", "product_image_url",
  "potential_benefits", "research_fields",
  "pubmed_query", "pubmed_count", "pubmed_last_checked", "pubchem_cid",
  "updated_at", "created_at",
].join(",");

export function useCompounds() {
  return useQuery({
    queryKey: ["compounds"],
    queryFn: async () => {
      const { data, error } = await supabase.from("compounds").select(COMPOUND_LIST_COLUMNS).order("name");
      if (error) throw error;
      return data as unknown as DbCompound[];
    },
  });
}

// The navbar search, the homepage hero search and its phone sheet match on these five fields only. The list query
// above is about 770 KB on the wire because of the long description; this one is about 26 KB, and the search
// components ask for it only once a visitor opens a search, so a page that never searches never downloads it.
const COMPOUND_SEARCH_COLUMNS = "id,slug,name,aliases,category";
export type CompoundSearchItem = Pick<DbCompound, "id" | "slug" | "name" | "aliases" | "category">;

export function useCompoundSearchIndex(enabled = true) {
  return useQuery({
    queryKey: ["compound-search-index"],
    queryFn: async () => {
      const { data, error } = await supabase.from("compounds").select(COMPOUND_SEARCH_COLUMNS).order("name");
      if (error) throw error;
      return data as unknown as CompoundSearchItem[];
    },
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

/** How many compound profiles the database holds, read live (a HEAD request, no rows). */
export function useCompoundCount() {
  return useQuery({
    queryKey: ["compound-count"],
    queryFn: async () => {
      const { count, error } = await supabase.from("compounds").select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? null;
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useCompoundBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["compound", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase.from("compounds").select("*").eq("slug", slug).maybeSingle();
      if (error) throw error;
      return data as DbCompound | null;
    },
    enabled: !!slug,
  });
}

export function useFeaturedCompounds() {
  return useQuery({
    queryKey: ["compounds", "featured"],
    queryFn: async () => {
      const { data, error } = await supabase.from("compounds").select(COMPOUND_LIST_COLUMNS).eq("featured", true).order("name");
      if (error) throw error;
      return data as unknown as DbCompound[];
    },
  });
}

/**
 * Columns anonymous clients may read from public.vendors. The table-level anon SELECT was revoked on
 * 2026-09-08 (Stripe billing identifiers were publicly readable), so select("*") now fails for anon.
 * Keep in sync with supabase/migrations/20260908100000_vendors_hide_billing_columns_from_anon.sql.
 */
export const VENDOR_PUBLIC_COLUMNS =
  "id,name,slug,description,website,short_name,logo,logo_url,brand_color,default_coupon,ref_param,payment_methods,shipping_regions,priority,rating,subscription_tier,active,epc_estimate,scrape_url,last_scraped_at,archived_at,created_at,updated_at";

/** Public-facing: only active vendors */
export function useVendors() {
  return useQuery({
    queryKey: ["vendors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendors").select(VENDOR_PUBLIC_COLUMNS).eq("active", true).order("priority").order("name");
      if (error) throw error;
      return data as unknown as DbVendor[];
    },
  });
}

/** Admin: ALL vendors including inactive */
export function useAllVendors() {
  return useQuery({
    queryKey: ["vendors", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vendors").select("*").order("priority").order("name");
      if (error) throw error;
      return data as DbVendor[];
    },
  });
}

export function useVendorBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["vendor", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase.from("vendors").select(VENDOR_PUBLIC_COLUMNS).eq("slug", slug).maybeSingle();
      if (error) throw error;
      return data as unknown as DbVendor | null;
    },
    enabled: !!slug,
  });
}

export function useListings(compoundId?: string, vendorId?: string, options?: { enabled?: boolean }) {
  const { data: activeVendors, isError: vendorsFailed } = useVendors();
  const activeVendorIds = activeVendors?.map(v => v.id);
  
  return useQuery({
    queryKey: ["listings", compoundId, vendorId, activeVendorIds],
    queryFn: async () => {
      // No vendor list means the vendors request failed. Without it no listing can be shown as belonging to an
      // active vendor, so the answer is an empty list. When the vendors do arrive the key changes and this runs
      // again with them.
      if (!activeVendorIds) return [] as DbListing[];
      let query = supabase.from("listings").select("*").eq("active", true);
      if (compoundId) query = query.eq("compound_id", compoundId);
      if (vendorId) query = query.eq("vendor_id", vendorId);
      if (activeVendorIds && activeVendorIds.length > 0) {
        query = query.in("vendor_id", activeVendorIds);
      }
      const { data, error } = await query.order("price");
      if (error) throw error;
      return data as DbListing[];
    },
    // Waits for the vendors. If that request fails this still settles (as an empty list): several pages hold
    // their loader until the listings are in, and a query that never runs stays pending for good.
    // options.enabled lets a caller that only needs the listings later (the navbar search) hold the request.
    enabled: (options?.enabled ?? true) && (!!activeVendorIds || vendorsFailed),
  });
}

export function useAllListings() {
  return useQuery({
    queryKey: ["listings", "all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("listings").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as DbListing[];
    },
  });
}

// Phase F (2026-04-27): Aggregate review stats for AggregateRating schema
// emission on CompoundPage / VendorPage / partner pages. The stats views
// (compound_review_stats, vendor_review_stats) filter to status='approved'
// upstream, so callers don't need to apply that filter here.
//
// Returns null when there are no approved reviews — letting Schema.org
// emitters skip the aggregateRating field entirely instead of shipping a
// `ratingValue: null` that would invalidate the rich-result eligibility.

export type ReviewStats = {
  review_count: number;
  avg_rating: number;
} | null;

export function useCompoundReviewStats(compoundId: string | undefined) {
  return useQuery({
    queryKey: ["compound-review-stats", compoundId],
    queryFn: async () => {
      if (!compoundId) return null;
      const { data, error } = await supabase
        .from("compound_review_stats" as any)
        .select("review_count, avg_rating")
        .eq("compound_id", compoundId)
        .maybeSingle();
      if (error) {
        // PGRST205 = view doesn't exist yet (migration not applied). Return
        // null so the caller's schema emitter just skips aggregateRating.
        if ((error as any).code === "PGRST205") return null;
        throw error;
      }
      return (data as unknown as ReviewStats) ?? null;
    },
    enabled: !!compoundId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useVendorReviewStats(vendorId: string | undefined) {
  return useQuery({
    queryKey: ["vendor-review-stats", vendorId],
    queryFn: async () => {
      if (!vendorId) return null;
      const { data, error } = await supabase
        .from("vendor_review_stats" as any)
        .select("review_count, avg_rating")
        .eq("vendor_id", vendorId)
        .maybeSingle();
      if (error) {
        if ((error as any).code === "PGRST205") return null;
        throw error;
      }
      return (data as unknown as ReviewStats) ?? null;
    },
    enabled: !!vendorId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeals() {
  return useQuery({
    queryKey: ["deals"],
    queryFn: async () => {
      const { data, error } = await supabase.from("deals").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as DbDeal[];
    },
  });
}

export function useVendorCommunityRatings() {
  return useQuery({
    queryKey: ["vendor-community-ratings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendor_reviews_public")
        .select("vendor_id, rating");
      if (error) throw error;
      const map: Record<string, { avg: number; count: number }> = {};
      for (const row of data) {
        const vid = row.vendor_id;
        const rating = row.rating;
        if (!vid || rating == null) continue;
        if (!map[vid]) map[vid] = { avg: 0, count: 0 };
        map[vid].count++;
        map[vid].avg += rating;
      }
      for (const key of Object.keys(map)) {
        map[key].avg = map[key].avg / map[key].count;
      }
      return map;
    },
  });
}

export type DbCoaScore = {
  id: string;
  vendor_id: string;
  compound_id: string;
  verified: boolean;
  lab_name: string | null;
  purity: number | null;
  test_date: string | null;
  grade: string;
};

export type CoaStats = {
  total: number;
  verified: number;
  latestDate: string | null;
  /** The laboratories named on the records we hold, deduplicated and sorted. */
  labs: string[];
  /** When a record for this vendor was last written or edited. */
  lastUpdated: string | null;
  /** How many of the records were read from a laboratory's own published list (source = 'lab_list'). */
  fromLabLists: number;
  /** Records with a test date in the last 12 months, and how many of those an editor has read. Not used by the
   *  Trust Score; kept for display. */
  recentTotal: number;
  recentVerified: number;
};

/** A test date counts as recent when it falls within the last 365 days. */
export const COA_RECENT_DAYS = 365;

// 2026-10-06: the certificate stats read coa_records (one row per certificate record, with its source) instead of
// vendor_coa_scores (one row per vendor and compound). The migration 20261006150000 copied the 12 old rows in, so
// the counts never drop; the lab-list imports add to them. "verified" here means read_by_editor, as before.
type CoaRecordRow = {
  vendor_id: string | null;
  read_by_editor: boolean;
  test_date: string | null;
  lab_name: string | null;
  updated_at: string | null;
  source: string;
};

function statsFromRows(rows: CoaRecordRow[]): CoaStats {
  const total = rows.length;
  const verified = rows.filter(d => d.read_by_editor).length;
  const dates = rows.map(d => d.test_date).filter(Boolean) as string[];
  const latestDate = dates.length > 0 ? dates.sort().reverse()[0] : null;
  const labs = [...new Set(rows.map(d => d.lab_name).filter(Boolean) as string[])].sort();
  const updates = rows.map(d => d.updated_at).filter(Boolean) as string[];
  const lastUpdated = updates.length > 0 ? updates.sort().reverse()[0] : null;
  const fromLabLists = rows.filter(d => d.source === "lab_list").length;
  const cutoff = new Date(Date.now() - COA_RECENT_DAYS * 86400000).toISOString().slice(0, 10);
  const recent = rows.filter(d => d.test_date && d.test_date.slice(0, 10) >= cutoff);
  return { total, verified, latestDate, labs, lastUpdated, fromLabLists, recentTotal: recent.length, recentVerified: recent.filter(d => d.read_by_editor).length };
}

export function useVendorCoaStats(vendorId: string | undefined) {
  return useQuery({
    queryKey: ["coa-stats", vendorId],
    queryFn: async () => {
      if (!vendorId) return null;
      const { data, error } = await supabase
        .from("coa_records" as any)
        .select("vendor_id, read_by_editor, test_date, lab_name, updated_at, source")
        .eq("vendor_id", vendorId);
      if (error) throw error;
      return statsFromRows((data ?? []) as unknown as CoaRecordRow[]);
    },
    enabled: !!vendorId,
  });
}

export type DbCompoundCoaRecord = {
  vendor_id: string | null;
  verified: boolean;
  lab_name: string | null;
  test_date: string | null;
};

/** The third-party certificate records we hold for one compound, across every vendor. */
export function useCompoundCoaRecords(compoundId: string | undefined) {
  return useQuery({
    queryKey: ["coa-records", "compound", compoundId],
    queryFn: async () => {
      if (!compoundId) return [] as DbCompoundCoaRecord[];
      const { data, error } = await supabase
        .from("coa_records" as any)
        .select("vendor_id, read_by_editor, lab_name, test_date")
        .eq("compound_id", compoundId);
      if (error) throw error;
      return ((data ?? []) as unknown as { vendor_id: string | null; read_by_editor: boolean; lab_name: string | null; test_date: string | null }[]).map(r => ({
        vendor_id: r.vendor_id,
        verified: r.read_by_editor,
        lab_name: r.lab_name,
        test_date: r.test_date,
      }));
    },
    enabled: !!compoundId,
  });
}

export function useAllVendorCoaStats() {
  return useQuery({
    queryKey: ["coa-stats", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coa_records" as any)
        .select("vendor_id, read_by_editor, test_date, lab_name, updated_at, source")
        .not("vendor_id", "is", null);
      if (error) throw error;
      const byVendor: Record<string, CoaRecordRow[]> = {};
      for (const row of (data ?? []) as unknown as CoaRecordRow[]) {
        if (!row.vendor_id) continue;
        (byVendor[row.vendor_id] ||= []).push(row);
      }
      const map: Record<string, CoaStats> = {};
      for (const [vendorId, rows] of Object.entries(byVendor)) map[vendorId] = statsFromRows(rows);
      return map;
    },
  });
}

export function useUserPriceAlerts(userId: string | undefined) {
  return useQuery({
    queryKey: ["price-alerts", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("price_alerts")
        .select("*, compounds(name, slug)")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!userId,
  });
}
